/**
 * Global modals redux.
 *
 * To add a modal state just add an entry on the `initialState`.
 *
 * If using `useGlobalModal` hoook is not necessary to add the action to the export list,
 * the hook uses just the `actions` export.
 */

import {
  CollectionColor,
  CollectionType,
  CollectionVisualRepresentationType,
  DeviceList_SmaInfoFragment,
  SmaMetricName,
  WriteExecutionLogEntityType,
  WriteExecutionLogSeverity,
} from '@/__generated__/graphql';

/** Config SMA tal como la expone el fragmento del listado (query-scoped, sin `reports`). */
type SmaConfigItem = DeviceList_SmaInfoFragment['smaConfig'][number];
import { type DeviceAlertsModalTab } from '@features/deviceAlertsModal/types';
import { DeviceDGAModalTab } from '@features/dga/types/modalTabs';
import { DownloadDataInput } from '@features/downloadData/types';
import { CreateReportsDefaultData } from '@features/reports2/types';
import { CreateModalStep } from '@features/reports2/utils/createModalStep';
import { UserConfigurationModalTab } from '@features/userConfigurationModal/components/types';
import { DeviceAccessModalTab } from '@features/deviceAccessModal/components/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

/**
 * @example
 * ModalStateRequiredKey<'deviceId', string> = {
 *   open: true;
 *   deviceId: string;
 * } | {
 *   open: false;
 * }
 */
type ModalStateRequiredKey<TKey extends string, TValue> =
  | ({
    open: true;
  } & {
    [K in TKey]: TValue;
  })
  | {
    open: false;
  };

/**
 * @example
 * ModalStateOptionalKey<'deviceId', string> = {
 *   open: boolean;
 *   deviceId?: string;
 * }
 */
type ModalStateOptionalKey<TKey extends string, TValue> = {
  open: boolean;
} & {
  [K in TKey]?: TValue;
};

type ClosedModal = { open: false };
type OpenModal<T> = { open: true } & T;

const initialState = {
  updateDeviceAlertsModal: {
    open: false,
  } as ModalStateOptionalKey<'deviceId', string>,
  createDeviceObservationModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      deviceId: string;
      initialContent?: string;
    }>,
  updateDeviceObservationModal: {
    open: false,
  } as ModalStateRequiredKey<'observationId', number>,
  deleteDeviceObservationModal: {
    open: false,
  } as ModalStateRequiredKey<'observationId', number>,
  updateDeviceDataConfigurationModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  /** @deprecated */
  deprecatedCreateMeasurementModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  /** @deprecated */
  deprecatedMeasurementsTableModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  upsertSmaConfigurationModal: {
    open: false,
  } as
    | {
      open: true;
      mode: 'update' | 'create';
      initialSmaConfig?: SmaConfigItem;
      initialDeviceId?: string;
    }
    | {
      open: false;
      mode?: 'update' | 'create';
      initialSmaConfig?: SmaConfigItem;
      initialDeviceId?: string;
    },
  smaSuccessTableModal: {
    open: false,
  } as
    | {
      open: false;
    }
    | {
      open: true;
      deviceId: string;
      smaUnitId: string;
      smaDeviceId: string;
      smaProcessId: string;
      smaMetricName: string;
    },
  smaReportsTableModal: {
    open: false,
  } as
    | {
      open: false;
    }
    | {
      open: true;
      deviceId: string;
      smaUnitId: string;
      smaDeviceId: string;
      smaProcessId: string;
      smaMetricName: SmaMetricName;
    },
  upsertAlertAdvancedCreationModal: {
    open: false,
  } as
    | {
      open: true;
      mode: 'update' | 'create';
      initialDeviceId?: string;
    }
    | {
      open: false;
      mode?: 'update' | 'create';
      initialDeviceId?: string;
    },

  saveUserModal: {
    open: false,
  } as ModalStateOptionalKey<'userId', number>,
  simulateUserModal: {
    open: false,
  } as ModalStateRequiredKey<'userId', number>,
  /** Simulación de usuario abierta por shortcut global — resuelve el usuario internamente. */
  simulateUserShortcutModal: {
    open: false as boolean,
  },
  deleteUserModal: {
    open: false,
  } as ModalStateRequiredKey<'userId', number>,
  userProfileModal: {
    open: false,
  } as ModalStateRequiredKey<'userId', number>,
  createTemporaryUserModal: {
    open: false as boolean,
  },
  createCollectionModal: {
    open: false as boolean,
  },
  // Sin payload: el id de la collection ORGANIZATION que necesita el picker de usuarios llega al
  // modal como prop desde HomeAmaru, no por acá — quien despacha la apertura es
  // useNewCollectionEligibility (AMARU-731) y no tiene la lista de colecciones a mano.
  createMyCollectionModal: {
    open: false as boolean,
  },
  updateCollectionModal: {
    open: false as boolean,
  } as | ClosedModal
    | OpenModal<{
      collection: {
        id: number;
        name: string;
        type: CollectionType;
        organizationId: number;
        description?: string | null;
        kmlUrl?: string | null;
        imageUrl?: string | null;
        visualRepresentationType?: CollectionVisualRepresentationType | null;
        color?: CollectionColor | null;
      };
    }>,
  deleteCollectionModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      collection: {
        id: number;
        name: string;
      };
    }>,
  // AMARU-822: crear el tablero operacional de una coleccion. Solo `id` y `name` — el conteo y la
  // lista de dispositivos los pide el propio modal con GET_COLLECTION_DEVICES, para que no queden
  // dos fuentes del mismo dato (la fila y el modal) pudiendo divergir.
  createMetricsViewFromCollectionModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      collection: {
        id: number;
        name: string;
      };
    }>,
  // AMARU-754: reemplaza las 4 keys previas (updateDevicesCollectionsRelationsModal,
  // updateAccountsCollectionsRelationsModal, addDevicesToCollectionDialogModal,
  // addAccountsToCollectionDialogModal) — un único componente genérico (AccessRelationManagerModal)
  // parametrizado por entityType para ambos modos (manage/add).
  manageCollectionAccessModal: {
    open: false as boolean,
  } as
    | ClosedModal
    | OpenModal<{
      collection: {
        id: number;
        name: string;
      };
      entityType: 'devices' | 'accounts';
    }>,
  addCollectionAccessModal: {
    open: false as boolean,
  } as
    | ClosedModal
    | OpenModal<{
      collection: {
        id: number;
        name: string;
      };
      entityType: 'devices' | 'accounts';
      excludedDeviceIds?: string[];
      excludedAccountIds?: number[];
    }>,
  extendTemporaryUserModal: {
    open: false,
  } as ModalStateOptionalKey<'userId', number>,
  extendTemporaryUserTokenModal: {
    open: false,
  } as ModalStateOptionalKey<'userId', number>,
  saveHardwareConfigurationModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  updateDGAConfigurationModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      deviceId: string;
      selectedTab?: DeviceDGAModalTab;
    }>,
  deleteDGAConfigurationModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  createDeviceModal: {
    open: false as boolean,
  },
  updateDeviceConfigurationModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  deviceAccessModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      deviceId: string;
      selectedTab?: DeviceAccessModalTab;
    }>,
  updateDeviceAliasesModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  updateDeviceMainMetricModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  cloneDeviceModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  addDeviceUserRelationsModal: {
    open: false,
  } as
    | {
      open: false;
    }
    | ({
      open: true;
    } & (
        | {
          fixedDeviceId?: string;
        }
        | {
          fixedUserId?: number;
        }
      )),
  removeDeviceUserRelationsModal: {
    open: false,
  } as
    | {
      open: false;
    }
    | ({
      open: true;
    } & (
        | {
          fixedDeviceId?: string;
        }
        | {
          fixedUserId?: number;
        }
      )),
  devicesGraveyardModal: {
    open: false,
  },
  usersGraveyardModal: {
    open: false,
  },
  viewEntityGroupedIssues: {
    open: false,
  } as
    | { open: false }
    | {
      open: true;
      selectedSeverityFilter?: WriteExecutionLogSeverity;
      entity: {
        type: WriteExecutionLogEntityType;
        id: number;
      };
    },
  deviceAlerts: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      deviceId: string;
      selectedTab?: DeviceAlertsModalTab;
    }>,
  alertsConfiguration: { open: false } as
    | ClosedModal
    | OpenModal<{
        deviceId?: string;
        step?: number;
      }>,
  deviceAlertsRawConfiguration: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  disableDeviceAlertsConfiguration: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  userConfigurationModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      userId: number;
      selectedTab?: UserConfigurationModalTab;
    }>,
  downloadData: {
    open: false,
  } as ClosedModal | OpenModal<DownloadDataInput>,
  reportProblem: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      selectableDeviceIds?: string[];
      defaultDeviceId?: string;
    }>,
  userPreferencesModal: {
    open: false,
  } as ModalStateOptionalKey<'initialUserId', number>,
  organizationPreferencesModal: {
    open: false,
  } as ModalStateOptionalKey<'initialOrganizationId', number>,
  dgaTicketModal: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<
      | {
        selectableDeviceIds?: string[];
        initialDeviceId?: string;
      }
      | {
        initialDeviceId: string;
      }
    >,
  createReports: {
    open: false,
  } as
    | ClosedModal
    | OpenModal<{
      templateId?: number;
      step?: CreateModalStep;
      defaultData?: CreateReportsDefaultData;
    }>,
  userActivityLogs: {
    open: false,
  } as ModalStateOptionalKey<'initialUserId', number>,
  moveGateModal: {
    open: false,
  } as ModalStateRequiredKey<'gateId', string>,
  gateOperationModal: {
    open: false,
  } as ModalStateRequiredKey<'movementId', number>,
  afterUpdateTasksModal: {
    open: false,
  } as ModalStateRequiredKey<'deviceId', string>,
  updateMultipleDevicesTagsModal: {
    open: false as boolean,
  },
  massiveDGARetryModal: {
    open: false,
  },
  resendCredentials: {
    open: false,
  } as ModalStateRequiredKey<'userId', number>,
  triggerConfigUpdateModal: {
    open: false,
  } as ClosedModal | OpenModal<{ deviceId: string }>,
  configurationFileHistoryModal: {
    open: false,
  } as ClosedModal | OpenModal<{ deviceId: string }>,
};

export type ModalsState = typeof initialState;

type SetStateAction<TKey extends keyof ModalsState> = PayloadAction<ModalsState[TKey]>;

/**
 * Generic set state action.
 */
const setModalState =
  <TKey extends keyof ModalsState>(key: TKey) =>
    (state: ModalsState, action: SetStateAction<TKey>) => {
      state[key] = action.payload;
    };

/**
 * Generate reducers from the `initialState` object
 */
const reducers = (Object.keys(initialState) as Array<keyof ModalsState>).reduce(
  (prev, key) => {
    const formattedKey = `set${key.charAt(0).toUpperCase()}${key.slice(1)}` as keyof typeof prev;
    prev[formattedKey] = setModalState(key);
    return prev;
  },
  {} as {
    [K in keyof ModalsState as `set${Capitalize<K>}`]: ReturnType<typeof setModalState<K>>;
  },
);

const slice = createSlice({
  name: 'modals_store',
  initialState,
  reducers,
});

export default slice.reducer;
export const actions = { ...slice.actions };
export const {
  setUpdateDeviceAlertsModal,
  setCreateDeviceObservationModal,
  setUpdateDeviceObservationModal,
  setDeleteDeviceObservationModal,
  setUpdateDeviceDataConfigurationModal,
  setDeprecatedMeasurementsTableModal,
  setDeprecatedCreateMeasurementModal,
  setUpsertSmaConfigurationModal,
  setSmaSuccessTableModal,
  setSmaReportsTableModal,
  setUpsertAlertAdvancedCreationModal,
  setSaveUserModal,
  setSimulateUserModal,
  setSimulateUserShortcutModal,
  setDeleteUserModal,
  setUserProfileModal,
  setCreateTemporaryUserModal,
  setCreateCollectionModal,
  setCreateMyCollectionModal,
  setUpdateCollectionModal,
  setDeleteCollectionModal,
  setCreateMetricsViewFromCollectionModal,
  setManageCollectionAccessModal,
  setAddCollectionAccessModal,
  setExtendTemporaryUserModal,
  setExtendTemporaryUserTokenModal,
  setSaveHardwareConfigurationModal,
  setUpdateDGAConfigurationModal,
  setDeleteDGAConfigurationModal,
  setUpdateDeviceConfigurationModal,
  setUpdateDeviceAliasesModal,
  setUpdateDeviceMainMetricModal,
  setAlertsConfiguration,
  setMassiveDGARetryModal,
} = slice.actions;
